import styled from '@emotion/styled'
import {
    ApiShopperLandingPageLink,
    BrandChatCard,
} from '@storefronts/common-types'
import { RefObject, useContext } from 'react'
import {
    ACCENT_FILL_COLOR_VAR,
    ACCENT_TEXT_COLOR_VAR,
    BUTTON_BORDER_RADIUS_VAR,
    PRIMARY_DISCOUNT_COLOR_VAR,
} from '../../context/CustomizationContextProvider'
import { PageContext } from '../../context/PageContextProvider'
import { AnalyticsContext } from '../../context/analyticsContext'
import { ViddyLPContext } from '../../context/viddyLPContext'
import { imageUrlTransform } from '../../utils/imageUrlTransform'
import { priceFormat } from '../../utils/priceFormat'
import {
    getDisplayData,
    resolveProductCardImageUrl,
} from '../../utils/productDisplay'
import { useProductGridVariantSelection } from '../shared/variant-options/useProductGridVariantSelection'
import { BrandAvatar } from './BrandAvatar'

/**
 * The one product a reply is about, shown big with the shopper's needs ticked
 * off. Price and discount come from the same hook as the storefront's grid
 * card; Shop now opens the storefront's product drawer.
 */
export const BrandChatHeroCard: React.FC<{
    card: BrandChatCard
    link: ApiShopperLandingPageLink
    cardRef?: RefObject<HTMLDivElement>
}> = ({ card, link, cardRef }) => {
    const { landingPage } = useContext(ViddyLPContext)
    const { setActiveLPLink } = useContext(PageContext)
    const { Analytics } = useContext(AnalyticsContext)
    const { activeVariant, price, comparePrice } =
        useProductGridVariantSelection({
            lpLink: link,
            showSwatches: false,
            preferAvailable: true,
        })
    const displayData = getDisplayData(
        link,
        activeVariant?.shopifyProductId ?? null,
    )
    const image = imageUrlTransform(
        resolveProductCardImageUrl(activeVariant, link, displayData),
    )
    const currencySymbol = landingPage.client.currencySymbol
    const open = () => {
        Analytics.TRACK_BRAND_CHAT_PRODUCT_CLICK(link, 'hero_card')
        setActiveLPLink(link, {
            initialVariantShopifyId: activeVariant?.shopifyId ?? null,
        })
    }

    return (
        <Card ref={cardRef}>
            <Hero type="button" onClick={open}>
                {image && <HeroImage src={image} alt="" />}
                <HeroShade aria-hidden />
                <Tag>
                    <BrandAvatar client={landingPage.client} size={18} />
                    Made for your chat
                </Tag>
                <HeroText>
                    <ProductName>{displayData.title ?? link.title}</ProductName>
                    <Headline>{card.headline}</Headline>
                </HeroText>
            </Hero>
            <Matches aria-label="What you're looking for">
                {card.matches.map((match, index) => (
                    <Match
                        key={match.need}
                        style={{ animationDelay: `${240 + index * 140}ms` }}
                    >
                        <Tick aria-hidden>✓</Tick>
                        <MatchText>
                            <Need>{match.need}</Need>
                            <Answer>{match.answer}</Answer>
                        </MatchText>
                    </Match>
                ))}
            </Matches>
            <Buy>
                <PriceBlock>
                    {price !== undefined && (
                        <Price>
                            {priceFormat(price, currencySymbol)}
                            {comparePrice != null && comparePrice > price && (
                                <s>
                                    {priceFormat(comparePrice, currencySymbol)}
                                </s>
                            )}
                        </Price>
                    )}
                    {card.offer && <Offer>{card.offer}</Offer>}
                </PriceBlock>
                <ShopButton type="button" onClick={open}>
                    Shop now
                </ShopButton>
            </Buy>
        </Card>
    )
}

const Card = styled.div`
    border-radius: 24px;
    overflow: hidden;
    background: #ffffff;
    border: 1px solid #ececec;
    box-shadow: 0 18px 40px -24px rgba(0, 0, 0, 0.35);
    animation: cardIn 520ms cubic-bezier(0.2, 0.8, 0.2, 1) both;

    @keyframes cardIn {
        from {
            transform: translateY(16px) scale(0.97);
            opacity: 0;
        }
        to {
            transform: none;
            opacity: 1;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`

const Hero = styled.button`
    position: relative;
    display: block;
    width: 100%;
    aspect-ratio: 5 / 4;
    max-height: 300px;
    padding: 0;
    border: 0;
    background: #f2f2f2;
    cursor: pointer;
    overflow: hidden;
    text-align: left;
    color: #ffffff;
    font: inherit;
`

const HeroImage = styled.img`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
`

const HeroShade = styled.div`
    position: absolute;
    inset: 0;
    background: linear-gradient(
        180deg,
        rgba(0, 0, 0, 0) 40%,
        rgba(0, 0, 0, 0.72) 100%
    );
`

const Tag = styled.span`
    position: absolute;
    top: 12px;
    left: 12px;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 10px 5px 5px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.86);
    backdrop-filter: blur(12px);
    color: #0d0d0d;
    font-size: 12px;
    font-weight: 600;
`

const HeroText = styled.div`
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 14px;
`

const ProductName = styled.div`
    font-size: 13px;
    font-weight: 500;
    opacity: 0.85;
`

const Headline = styled.div`
    margin-top: 4px;
    font-size: 24px;
    line-height: 1.05;
    font-weight: 700;
    letter-spacing: -0.025em;
    text-wrap: balance;
`

const Matches = styled.ul`
    list-style: none;
    margin: 0;
    padding: 2px 14px;
`

const Match = styled.li`
    display: grid;
    grid-template-columns: 22px 1fr;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    border-bottom: 1px solid #f0f0f0;
    animation: matchIn 360ms ease-out both;

    &:last-of-type {
        border-bottom: 0;
    }

    @keyframes matchIn {
        from {
            opacity: 0;
            transform: translateX(-6px);
        }
        to {
            opacity: 1;
            transform: none;
        }
    }

    @media (prefers-reduced-motion: reduce) {
        animation: none;
    }
`

const Tick = styled.span`
    width: 22px;
    height: 22px;
    border-radius: 999px;
    display: grid;
    place-items: center;
    font-size: 12px;
    font-weight: 700;
    background: var(${ACCENT_FILL_COLOR_VAR}, #0d0d0d);
    color: var(${ACCENT_TEXT_COLOR_VAR}, #ffffff);
`

const MatchText = styled.span`
    display: flex;
    flex-direction: column;
    gap: 1px;
    min-width: 0;
`

const Need = styled.span`
    font-size: 12px;
    color: #6e6e73;
`

const Answer = styled.span`
    font-size: 15px;
    font-weight: 600;
`

const Buy = styled.div`
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 14px 12px;
    border-top: 1px solid #ececec;
`

const PriceBlock = styled.div`
    flex: 1;
    min-width: 0;
`

const Price = styled.div`
    display: flex;
    align-items: baseline;
    gap: 6px;
    font-size: 18px;
    font-weight: 700;

    s {
        font-size: 13px;
        font-weight: 400;
        color: #aeaeb2;
    }
`

const Offer = styled.div`
    margin-top: 2px;
    font-size: 12px;
    font-weight: 600;
    color: var(${PRIMARY_DISCOUNT_COLOR_VAR}, #1a7f37);
`

const ShopButton = styled.button`
    flex: none;
    appearance: none;
    border: 0;
    border-radius: var(${BUTTON_BORDER_RADIUS_VAR}, 999px);
    padding: 10px 20px;
    font: inherit;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    background: var(${ACCENT_FILL_COLOR_VAR}, #0d0d0d);
    color: var(${ACCENT_TEXT_COLOR_VAR}, #ffffff);
`
